import {
  type Appointment,
  type Customer,
  asociateAppointment,
  bookFollowUpAppointment,
  useTeam,
} from "@repo/api";
import {
  Button,
  FormField,
  ListView,
  ListViewItem,
  Option,
  Select,
  Text,
  calendarIcon,
  mailIcon,
  personIcon,
  phoneIcon,
} from "@repo/skapa";
import { t, useTranslation } from "@repo/translation";
import {
  CoWorkerName,
  ModalSheet,
  SectionContainer,
  localizeDateTimeRange,
  translateServices,
  useSettings,
  useToast,
} from "@repo/ui";
import { trackAnalytics } from "@repo/utils";
import { useEffect, useMemo, useState } from "react";

interface BookFollowUpModalProps {
  isOpen: boolean;
  originalAppointment: Appointment;
  followUpAppointmentId?: number;
  customer?: Customer;
  startTime?: Date;
  endTime?: Date;
  selectedCoworker?: string;
  selectedTeam?: number;
  projectId?: number;
  onConfirm: () => void;
  onCancelFlow: () => void;
}

export const BookFollowUpModal = ({
  isOpen,
  originalAppointment,
  followUpAppointmentId,
  customer,
  startTime,
  endTime,
  selectedCoworker,
  selectedTeam,
  projectId,
  onConfirm,
  onCancelFlow,
}: BookFollowUpModalProps) => {
  const { dateFnsLocale } = useTranslation();
  const { updateToast } = useToast();
  const { boka_config } = useSettings();
  const { data: team } = useTeam(selectedTeam);
  const [isBooking, setIsBooking] = useState(false);
  const [selectedService, setSelectedService] = useState<string>();

  const enabledServices = useMemo(() => {
    const storeConfig = boka_config?.stores?.find(
      (store) => store.unit_id_key === team?.buCode
    );

    return storeConfig?.service_configs?.map((s) => s.service_name_key) ?? [];
  }, [boka_config?.stores, team?.buCode]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedService(undefined);
      setIsBooking(false);
      return;
    }
    setSelectedService((current) =>
      current && enabledServices.includes(current)
        ? current
        : enabledServices[0]
    );
  }, [enabledServices, isOpen]);

  const customerName = customer
    ? [customer.firstName, customer.lastName].filter(Boolean).join(" ") || "-"
    : "-";

  const followUpTimeLabel = startTime
    ? localizeDateTimeRange(startTime, endTime, dateFnsLocale)
    : "-";

  const handleConfirm = async () => {
    const orderNumber = originalAppointment.orderNumber;

    if (
      !orderNumber ||
      !followUpAppointmentId ||
      (enabledServices.length > 0 && !selectedService)
    ) {
      updateToast({
        type: "error",
        title: t("prompt.failed_to_book_follow_up_appointment"),
      });
      return;
    }

    setIsBooking(true);
    let followUpAppointment: Awaited<
      ReturnType<typeof bookFollowUpAppointment>
    >;
    try {
      followUpAppointment = await bookFollowUpAppointment(
        orderNumber,
        followUpAppointmentId,
        selectedService
      );
    } catch {
      updateToast({
        type: "error",
        title: t("prompt.failed_to_book_follow_up_appointment"),
      });
      setIsBooking(false);
      return;
    }

    if (!followUpAppointment?.orderNumber) {
      updateToast({
        type: "error",
        title: t("prompt.failed_to_book_follow_up_appointment"),
      });
      setIsBooking(false);
      return;
    }

    if (projectId) {
      try {
        await asociateAppointment(projectId, followUpAppointment.orderNumber);
      } catch {
        trackAnalytics("book_follow_up_link_failed");
      }
    }

    updateToast({ title: t("prompt.follow_up_appointment_booked") });
    setIsBooking(false);
    onConfirm();
  };

  return (
    <ModalSheet
      title={t("action.book_follow_up_appointment")}
      visible={isOpen}
      onClose={onCancelFlow}
      footer={
        <>
          <Button
            type="primary"
            text={t("action.confirm_follow_up_booking")}
            loading={isBooking}
            onClick={handleConfirm}
          />
          <Button
            type="secondary"
            text={t("action.cancel")}
            onClick={onCancelFlow}
          />
        </>
      }
    >
      <SectionContainer>
        <ListView id="follow-up-appointment-details" showDivider={false}>
          <ListViewItem
            leadingIcon={calendarIcon}
            title={t("label.date_and_time")}
            description={followUpTimeLabel}
          />
          <ListViewItem
            leadingIcon={personIcon}
            title={t("label.coworker")}
            description={
              selectedCoworker ? <CoWorkerName email={selectedCoworker} /> : "-"
            }
          />
        </ListView>

        <FormField>
          <Select
            id="follow-up-service-select"
            label={t("label.select_service_for_follow_up_appointment")}
            value={selectedService}
            required={enabledServices.length > 0}
            onChange={(e) => setSelectedService(e.target.value)}
          >
            {enabledServices.map((service) => (
              <Option
                key={service}
                value={service}
                name={translateServices(service)}
              />
            ))}
          </Select>
        </FormField>

        <Text textStyle="Heading.XS">{t("label.customer")}</Text>
        <ListView id="follow-up-customer-details" showDivider={false}>
          <ListViewItem
            leadingIcon={personIcon}
            title={t("label.name")}
            description={customerName}
          />
          {customer?.phoneNumber && (
            <ListViewItem
              leadingIcon={phoneIcon}
              title={t("label.phone_number")}
              description={customer.phoneNumber}
            />
          )}
          {customer?.email && (
            <ListViewItem
              leadingIcon={mailIcon}
              title={t("label.email")}
              description={customer.email}
            />
          )}
          {customer?.language && (
            <ListViewItem
              leadingIcon={personIcon}
              title={t("label.language")}
              description={customer.language}
            />
          )}
        </ListView>
      </SectionContainer>
    </ModalSheet>
  );
};
